import { DatesRangeValue } from '@mantine/dates';
import { createEffect, createEvent, Effect, EventCallable, sample } from 'effector';
import { saveAs } from 'file-saver';
import { jsPDF as JsPDF, jsPDFOptions } from 'jspdf';
import autoTable, { RowInput, UserOptions } from 'jspdf-autotable';
import XLSX from 'xlsx-js-style';

export const EXPORT_FORMAT = {
	xlsx: 'xlsx',
	pdf: 'pdf',
} as const satisfies Record<string, string>;

export type ExportFormat = (typeof EXPORT_FORMAT)[keyof typeof EXPORT_FORMAT];

export type SaveAsExcelParams = {
	data: (string | number)[][];
	sheetTitle: string;
	filenameTemplate: string;
	dates: DatesRangeValue<string>;
	columnWidths?: (number | { wch: number } | undefined)[];
	enableBorders?: boolean;
};

export type SaveAsPdfParams = {
	head: RowInput[];
	body: RowInput[];
	footer: RowInput[];
	filenameTemplate: string;
	dates: DatesRangeValue<string>;
	tableOptions?: Omit<UserOptions, 'head' | 'body' | 'footer'>;
	documentOptions?: jsPDFOptions;
};

export type ExportReportModel = {
	exportPdfClicked: EventCallable<void>;
	exportExcelClicked: EventCallable<void>;
	exportButtonClicked: EventCallable<ExportFormat>;
	saveAsExcelFx: Effect<SaveAsExcelParams, void>;
	saveAsPdfFx: Effect<SaveAsPdfParams, void>;
};

export const createExportReportModel = (): ExportReportModel => {
	const exportPdfClicked = createEvent();

	const exportExcelClicked = createEvent();

	const exportButtonClicked = createEvent<ExportFormat>();

	const saveAsExcelFx = createEffect<SaveAsExcelParams, void>(
		({ data, dates, filenameTemplate, sheetTitle, columnWidths, enableBorders }) => {
			const worksheet = XLSX.utils.aoa_to_sheet(data);

			const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
			const colCount = range.e.c - range.s.c + 1;

			const autoWidths: number[] = Array(colCount).fill(10);

			for (let C = range.s.c; C <= range.e.c; C += 1) {
				let maxWidth = 10;

				for (let R = range.s.r; R <= range.e.r; R += 1) {
					const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
					const cell = worksheet[cellAddress];

					if (cell && cell.v !== undefined) {
						const cellValue = String(cell.v);
						const cellWidth = cellValue.length + 2;

						maxWidth = Math.max(maxWidth, cellWidth);
					}
				}

				autoWidths[C] = Math.min(maxWidth, 50);
			}

			worksheet['!cols'] = autoWidths.map((autoWidth, index) => {
				const specifiedWidth = columnWidths?.[index];

				if (specifiedWidth === undefined) {
					return { wch: autoWidth };
				}

				return typeof specifiedWidth === 'number' ? { wch: specifiedWidth } : specifiedWidth;
			});

			if (enableBorders) {
				const borderStyle = {
					top: { style: 'thin' },
					bottom: { style: 'thin' },
					left: { style: 'thin' },
					right: { style: 'thin' },
				};

				for (let R = range.s.r; R <= range.e.r; R += 1) {
					for (let C = range.s.c; C <= range.e.c; C += 1) {
						const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
						const cell = worksheet[cellAddress];

						if (cell) {
							cell.s = { ...cell.s, border: borderStyle };
						} else {
							worksheet[cellAddress] = { v: '', t: 's', s: { border: borderStyle } };
						}
					}
				}
			}

			const workbook = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(workbook, worksheet, sheetTitle);

			const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
			const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });

			const datePart = dates[0] && dates[1] ? `${dates[0]}_${dates[1]}` : '';

			const fileName = filenameTemplate.replace('%DATE%', datePart ? `-${datePart}` : '');

			saveAs(blob, fileName);
		},
	);

	const saveAsPdfFx = createEffect<SaveAsPdfParams, void>(
		({ head, body, footer, dates, filenameTemplate, tableOptions, documentOptions }) => {
			const doc = new JsPDF(documentOptions);

			autoTable(doc, {
				head,
				body,
				foot: footer,
				...tableOptions,
			});

			const datePart = dates[0] && dates[1] ? `${dates[0]}_${dates[1]}` : '';

			const fileName = filenameTemplate.replace('%DATE%', datePart ? `-${datePart}` : '');

			doc.save(fileName);
		},
	);

	sample({
		clock: [
			exportPdfClicked.map(() => EXPORT_FORMAT.pdf),
			exportExcelClicked.map(() => EXPORT_FORMAT.xlsx),
		],
		target: exportButtonClicked,
	});

	return {
		exportPdfClicked,
		exportExcelClicked,
		saveAsExcelFx,
		saveAsPdfFx,
		exportButtonClicked,
	};
};
